import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faLayerGroup,
  faComments,
  faCalendar,
  faFileAlt,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import './TableauDeBord.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const TableauDeBord = () => {
  const [isDark, setIsDark] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark' ||
           document.body.classList.contains('dark-mode');
  });

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalWorkspaces: 0,
    totalMessages: 0,
    totalEvents: 0,
    totalForms: 0
  });

  const [userGrowth, setUserGrowth] = useState([]);
  const [topWorkspaces, setTopWorkspaces] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Watch for theme changes
  useEffect(() => {
    const checkTheme = () => {
      const dark = document.documentElement.getAttribute('data-theme') === 'dark' ||
                   document.body.classList.contains('dark-mode');
      setIsDark(dark);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'data-theme' || mutation.attributeName === 'class')) {
          checkTheme();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    observer.observe(document.body, { attributes: true });

    return () => observer.disconnect();
  }, []);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('week');

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetching dashboard data
      const response = await api.get(`/admin/platform/stats?range=${timeRange}`);
      // Dashboard data loaded
      
      if (response.data) {
        setStats({
          totalUsers: response.data.totalUsers || 0,
          activeUsers: response.data.activeUsers || 0,
          totalWorkspaces: response.data.totalWorkspaces || 0,
          totalMessages: response.data.totalMessages || 0,
          totalEvents: response.data.totalEvents || 0,
          totalForms: response.data.totalForms || 0
        });
        
        setUserGrowth(response.data.userGrowth || []);
        setTopWorkspaces(response.data.topWorkspaces || []);
        setRecentUsers(response.data.recentUsers || []);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('[DASHBOARD] Error:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Chart colors based on theme
  const textColor = isDark ? '#e5e7eb' : '#374151';
  const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';

  // Chart configuration
  const chartData = {
    labels: userGrowth.map(item => item.date),
    datasets: [
      {
        label: 'Nouveaux Utilisateurs',
        data: userGrowth.map(item => item.count),
        borderColor: '#3b82f6',
        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: isDark ? '#0d1117' : '#ffffff',
        pointBorderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { 
          color: textColor,
          font: { size: 12 }
        }
      },
      tooltip: {
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { 
          color: textColor,
          font: { size: 11 }
        },
        grid: { 
          color: gridColor,
          drawBorder: false
        }
      },
      x: {
        ticks: { 
          color: textColor,
          font: { size: 11 }
        },
        grid: { 
          color: gridColor,
          drawBorder: false
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h2>Tableau de Bord</h2>
          <p className="subtitle" style={{ color: 'var(--text-secondary)' }}>Vue d'ensemble de la plateforme Lonnii</p>
        </div>
        
        <div className="header-actions" style={{ display: 'flex', gap: '1rem' }}>
          <div className="time-selector" style={{ display: 'flex', gap: '0.5rem' }}>
            {['today', 'week', 'month', 'year'].map((range) => (
              <button 
                key={range}
                className={`btn ${timeRange === range ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setTimeRange(range)}
                style={{ textTransform: 'capitalize' }}
              >
                {range === 'today' ? "Aujourd'hui" : 
                 range === 'week' ? 'Semaine' : 
                 range === 'month' ? 'Mois' : 'Année'}
              </button>
            ))}
          </div>
          
          <button className="refresh-button" onClick={fetchData} disabled={loading}>
            <FontAwesomeIcon icon={faSync} spin={loading} style={{ marginRight: '0.5rem' }} /> Actualiser
          </button>
        </div>
      </div>

      {error && (
        <div className="error-alert" style={{ 
          padding: '1rem', 
          background: 'rgba(239, 68, 68, 0.1)', 
          color: 'var(--danger-color)', 
          borderRadius: 'var(--radius-md)',
          marginBottom: '2rem',
          border: '1px solid rgba(239, 68, 68, 0.2)'
        }}>
          <strong>Erreur:</strong> {error}
        </div>
      )}

      {/* KPI Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon-wrapper" style={{ background: 'rgba(124, 58, 237, 0.12)', color: '#7c3aed' }}>
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="stat-content">
            <h3>Utilisateurs</h3>
            <div className="stat-value">{stats.totalUsers.toLocaleString()}</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--success-color)', marginTop: '0.25rem' }}>
              {stats.activeUsers} actifs
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper" style={{ background: 'rgba(6, 182, 212, 0.12)', color: '#06b6d4' }}>
            <FontAwesomeIcon icon={faLayerGroup} />
          </div>
          <div className="stat-content">
            <h3>Espaces de Travail</h3>
            <div className="stat-value">{stats.totalWorkspaces.toLocaleString()}</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Groupes créés
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#d97706' }}>
            <FontAwesomeIcon icon={faComments} />
          </div>
          <div className="stat-content">
            <h3>Messages</h3>
            <div className="stat-value">{stats.totalMessages.toLocaleString()}</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Échangés
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
            <FontAwesomeIcon icon={faCalendar} />
          </div>
          <div className="stat-content">
            <h3>Événements</h3>
            <div className="stat-value">{stats.totalEvents.toLocaleString()}</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Calendrier
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper" style={{ background: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' }}>
            <FontAwesomeIcon icon={faFileAlt} />
          </div>
          <div className="stat-content">
            <h3>Formulaires</h3>
            <div className="stat-value">{stats.totalForms.toLocaleString()}</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Créés
            </p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="charts-section">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Croissance des Utilisateurs</h3>
          </div>
          <div style={{ height: '300px' }}>
            {userGrowth.length > 0 ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                Aucune donnée disponible pour cette période
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity / Top Workspaces */}
        <div className="list-card">
          <div className="list-header">
            <h3>Top Espaces de Travail</h3>
          </div>
          <div className="list-content">
            {topWorkspaces.length > 0 ? (
              topWorkspaces.map((workspace, idx) => (
                <div className="list-item" key={idx}>
                  <div className="user-info-mini">
                    <div className="avatar-circle" style={{ background: 'var(--secondary-color)' }}>
                      {workspace.name ? workspace.name.charAt(0).toUpperCase() : 'G'}
                    </div>
                    <div className="user-details">
                      <h4>{workspace.name || 'Sans nom'}</h4>
                      <p>{workspace.member_count || 0} membres</p>
                    </div>
                  </div>
                  <span className="date-badge">{formatDate(workspace.created_at)}</span>
                </div>
              ))
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                Aucun espace de travail
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Users Table */}
      <div className="list-card">
        <div className="list-header">
          <h3>Utilisateurs Récents</h3>
        </div>
        <div className="list-content">
          {recentUsers.length > 0 ? (
            recentUsers.map((user, idx) => (
              <div className="list-item" key={idx}>
                <div className="user-info-mini">
                  <div className="avatar-circle">
                    {user.firstname ? user.firstname.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div className="user-details">
                    <h4>
                      {user.firstname && user.lastname 
                        ? `${user.firstname} ${user.lastname}`
                        : user.username || 'Sans nom'}
                    </h4>
                    <p>{user.email}</p>
                  </div>
                </div>
                <span className="date-badge">{formatDate(user.created_at)}</span>
              </div>
            ))
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              Aucun utilisateur récent
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableauDeBord;
